import type {
  AwardsDef,
  BackgroundDef,
  ChallengesDef,
  CoreStatDef,
  GovernDef,
  MilestoneDef,
  PerkDef,
  RustClassDef,
  SkillDef,
  SkillRulesDef,
  StaminaDef,
  StartDef,
  StatAwardsDef,
  WorkoutWeightsDef,
} from './types';

/*
 * The C# initializers of shared-cs Content/SkillDefs.cs: an absent field parses to these. Lists and maps default EMPTY like the C#
 * (Newtonsoft appends into a non-empty default); the seed numbers for the fallbacks are the DEFAULT_* constants, read by the accessors
 * the same way `SkillRulesDef.RateAt` / `RustClassOf` / `WorkoutWeightsDef.EndOf` do.
 */

export const coreStatDefaults = (): CoreStatDef => ({
  id: '',
  name: '',
  short: '',
  text: '',
  learnSpeed: 0.3,
  rustClass: '',
  governs: [],
  ability5: null,
  ability7: '',
  signature10: null,
  affectsText: '',
  trainText: '',
});

export const skillDefaults = (): SkillDef => ({
  id: '',
  family: '',
  name: '',
  text: '',
  baseline: 0,
  learnSpeed: 1,
  rustClass: '',
  cantBelow: 0,
  governs: [],
  ability5: null,
  ability7: '',
  signature10: null,
  affectsText: '',
  trainText: '',
});

export const governDefaults = (): GovernDef => ({ key: '', perLevel: 0, table: [] });
export const milestoneDefaults = (): MilestoneDef => ({
  name: '',
  modifiers: [],
  flags: [],
  text: '',
});
export const perkDefaults = (): PerkDef => ({
  id: '',
  owner: '',
  name: '',
  text: '',
  modifiers: [],
  flags: [],
});
export const staminaDefaults = (): StaminaDef => ({
  baseS: 10,
  regenPerS: 0.6,
  windedS: 2,
  windedSpeedMult: 0.85,
  windedSpreadMult: 1.3,
  windedAcquireMult: 1.3,
});
export const rustClassDefaults = (): RustClassDef => ({
  perDay: [],
  floorPoints: 500,
  belowPeakPoints: 200,
});
export const statAwardsDefaults = (): StatAwardsDef => ({
  meleeKillStr: 1,
  haulStr: 10,
  haulKgPerMember: 8,
  extractedEnd: 6,
  waveEnd: 2,
  rangedKillRef: 1,
  stealthKillRef: 1,
  reviveRef: 2,
  searchWit: 1,
  buildWit: 5,
  waveNrv: 3,
  bigWinNrv: 10,
  revivedNrv: 5,
  recruitCha: 10,
  tradeCha: 3,
});
export const awardsDefaults = (): AwardsDef => ({
  kill: 3,
  headshot: 2,
  stealthKill: 6,
  search: 2,
  forcedDoor: 6,
  lockPicked: 8,
  treat: 6,
  revive: 10,
  throwLanded: 3,
  bigWin: 15,
  homeBuild: 20,
  homeDischarge: 20,
  stat: statAwardsDefaults(),
});
export const challengesDefaults = (): ChallengesDef => ({
  door: 4,
  lock: 5,
  revive: 6,
  treat: 3,
  search: 3,
});
export const workoutWeightsDefaults = (): WorkoutWeightsDef => ({
  endPerCreditedMin: {},
  strPerCreditedMin: {},
  meleePerCreditedMin: {},
  meleeSkills: [],
  meleeMaxLevel: 7,
});
export const startDefaults = (): StartDef => ({ playerSwaps: 1, recruitSwaps: 1, swapPoints: 100 });
export const skillRulesDefaults = (): SkillRulesDef => ({
  enabled: true,
  rate: [],
  dailyCapPoints: 60,
  homeDailyCapPoints: 40,
  challengeFloor: 0.2,
  challengeStepPerLevel: 0.2,
  muscleMemoryMult: 3,
  graceDays: 3,
  deathRustDays: 7,
  rustClasses: {},
  awards: awardsDefaults(),
  challenges: challengesDefaults(),
  workouts: workoutWeightsDefaults(),
  start: startDefaults(),
  homeSkillLearnPerWit: 0.05,
  reducedShare: 0.5,
  reducedConditions: [],
  noShareConditions: [],
  devPointsMult: 1,
  signatureHeadshot: 0.9,
  reloadMinS: 0.1,
  knockbackMaxM: 2,
  maxBonusLootRolls: 2,
});
export const backgroundDefaults = (): BackgroundDef => ({
  id: '',
  bias: {},
  jacket: '',
  weight: 1,
  legacyRole: '',
});

/** `SkillRulesDef.DefaultRate` (index = level 0..9). */
export const DEFAULT_RATE = [1.5, 1.5, 1.5, 1.0, 0.8, 0.6, 0.4, 0.25, 0.15, 0.08];
/** `SkillRulesDef.DefaultRustClasses`. */
export const DEFAULT_RUST_CLASSES: Record<string, Required<RustClassDef>> = {
  skill: {
    perDay: [0, 0, 0, 0, 0, 0.5, 1, 1.5, 2.5, 3.5, 5],
    floorPoints: 500,
    belowPeakPoints: 200,
  },
  body: {
    perDay: [0, 0, 0, 0, 0, 0.75, 1.5, 2.25, 3.75, 5.25, 7.5],
    floorPoints: 400,
    belowPeakPoints: 200,
  },
  mind: {
    perDay: [0, 0, 0, 0, 0, 0.25, 0.5, 0.75, 1.25, 1.75, 2.5],
    floorPoints: 500,
    belowPeakPoints: 200,
  },
};
/** `WorkoutWeightsDef.DefaultEnd` / `DefaultStr` / `DefaultMelee` / `DefaultMeleeSkills`. */
export const DEFAULT_WORKOUTS = {
  endPerCreditedMin: {
    walk: 0.5,
    hike: 0.8,
    run: 1.0,
    cycle: 0.9,
    swim: 1.0,
    hiit: 0.7,
    strength: 0.2,
    sport: 0.6,
    other: 0.4,
  },
  strPerCreditedMin: {
    walk: 0.1,
    hike: 0.3,
    run: 0.2,
    cycle: 0.2,
    swim: 0.4,
    hiit: 0.7,
    strength: 1.0,
    sport: 0.4,
    other: 0.3,
  },
  meleePerCreditedMin: { strength: 0.2, hiit: 0.2, hike: 0.1, swim: 0.1, sport: 0.1 },
  meleeSkills: ['blunt', 'blades'],
} as const;

/** `SkillRulesDef.RustClassIds()`: content's classes, or the default three when content lists none. */
export const rustClassIds = (r: SkillRulesDef): string[] =>
  r.rustClasses != null && Object.keys(r.rustClasses).length > 0
    ? Object.keys(r.rustClasses)
    : Object.keys(DEFAULT_RUST_CLASSES);
/** `SkillRulesDef.RustClassOf(id)`. */
export const rustClassOf = (r: SkillRulesDef, id: string): RustClassDef | null => {
  if (r.rustClasses != null && Object.keys(r.rustClasses).length > 0)
    return r.rustClasses[id] ?? null;
  return DEFAULT_RUST_CLASSES[id] ?? null;
};
